import React, { useState } from 'react';
import { defaultImage } from '@assets'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
} from 'react-native';
import { useNavigation, useTheme } from '@react-navigation/native';
import { SearchBar, Icon } from '@components';
import { Theme } from '@theme';
import { useContactMappingStore, useContactsListStore } from '@store';
import { ContactListItem, Scope } from '@utils';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLanguage } from '@context';

const ContactSearch: React.FC = () => {
  const navigation = useNavigation<any>();
  const theme = useTheme();
  const { t } = useLanguage();
  const styles = getStyles(theme);
  const [searchText, setSearchText] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<Scope>(Scope.PRIVATE);
  const [selectedContact, setSelectedContact] = useState<ContactListItem | null>(null);

  const { contacts } = useContactsListStore();
  const { getScope } = useContactMappingStore()
  

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.fullName?.toLowerCase().includes(searchText.toLowerCase());
    const matchesFilter = getScope(contact.id).includes(selectedFilter);
    return matchesSearch && matchesFilter;
  });

  const handleContactPress = (contact: ContactListItem) => {
    setSelectedContact(contact);
    navigation.navigate('AddKeep', { contact });
  };

  const renderTabButton = (tab: Scope, title: string, icon: string, color: string) => (
    <TouchableOpacity
      style={[
        styles.tabButton,
        selectedFilter === tab && styles.activeTabButton,
      ]}
      onPress={() => setSelectedFilter(tab)}
    >
      <Icon name={icon as any} size={16} color={selectedFilter === tab ? color : theme.colors.greyBlue} />
      <Text
        style={[
          styles.tabText,
          selectedFilter === tab && {color: color, fontWeight: 'bold'},
        ]}
      >
        {title}
      </Text>
      {selectedFilter === tab && <View style={[styles.activeTabIndicator, {backgroundColor: color}]} />}
    </TouchableOpacity>
  );

  const renderContactItem = (contact: ContactListItem) => (
    <TouchableOpacity
      key={contact.id}
      style={styles.contactItem}
      onPress={() => handleContactPress(contact)}
    >
      <Image source={contact.avatarUrl ? { uri: contact.avatarUrl } : defaultImage} style={styles.contactAvatar} />
      <View style={styles.contactInfo}>
        <Text style={styles.contactName}>{contact.fullName}</Text>
      </View>
      {selectedContact?.id === contact.id && <View style={styles.selectionBorder} />}
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Icon name="arrowBack" size={20} color={theme.colors.primary} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('keepReminder.searchContact')}</Text>
        <View style={styles.placeholder} />
      </View>

      <View style={styles.searchContainer}>
        <SearchBar
          value={searchText}
          onChangeText={setSearchText}
          placeholder={t('keepReminder.searchContact')}
          containerStyle={styles.searchBarContainer}
        />
      </View>

      <View style={styles.tabContainer}>
        {renderTabButton(Scope.PRIVATE, t('common.private'), 'lock', theme.colors.green)}
        {renderTabButton(Scope.PROFESSIONAL, t('common.pro'), 'work', theme.colors.primary)}
      </View>

      {/* Compteur de résultats uniforme */}
      <Text style={styles.sectionTitle}>
        {t('common.contactCount', { count: filteredContacts.length })}
      </Text>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {filteredContacts.map(renderContactItem)}
      </ScrollView>
    </SafeAreaView>
  );
};

const getStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.lightGrey,
  },
  backButton: {
    padding: theme.spacing.xs,
  },
  headerTitle: {
    fontSize: theme.fontSize.large,
    fontWeight: '600',
    color: theme.colors.text,
  },
  placeholder: {
    width: 40,
  },
  // Système de tabs comme dans Calendar
  tabContainer: {
    flexDirection: 'row',
    paddingHorizontal: 0,
    marginBottom: 0,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.lightGrey,
  },
  tabButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.lg,
    position: 'relative',
  },
  activeTabButton: {
    backgroundColor: 'transparent',
  },
  activeTabIndicator: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 2,
  },
  tabText: {
    fontSize: theme.fontSize.medium,
    fontWeight: '500',
    color: theme.colors.greyBlue,
    marginLeft: theme.spacing.sm,
  },
  searchContainer: {
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.colors.white,
  },
  searchBarContainer: {
    // Le SearchBar a déjà ses propres styles
  },
  sectionTitle: {
    fontSize: theme.fontSize.small,
    color: theme.colors.greyBlue,
    fontWeight: '500',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    textTransform: 'uppercase',
  },
  content: {
    flex: 1,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.lightGrey,
    position: 'relative',
  },
  contactAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: theme.spacing.md,
  },
  contactInfo: {
    flex: 1,
  },
  contactName: {
    fontSize: theme.fontSize.medium,
    fontWeight: '500',
    color: theme.colors.text,
    marginBottom: theme.spacing.xs,
  },
  contactTypeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  selectionBorder: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 4,
    backgroundColor: theme.colors.primary,
    borderTopLeftRadius: theme.border.small,
    borderBottomLeftRadius: theme.border.small,
  },

});

export default ContactSearch; 